import PartForm, {
  PartFormData,
} from '@/components/Operational/Inventory/Parts/PartForm';
import { usePartMultipleCreate } from '@/core/domain/operational/Parts/Parts.hooks';
import { PartFlag } from '@/core/domain/operational/Parts/Parts.types';
import { useCreateWorkOrderItemList } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { WorkOrderItem } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDisclosure } from '@/hooks';
import { Alert, Button, Col, Form, message, Modal, Row } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useWorkorderComplementBindItemsWithOperational } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import WorkOrderItemTypeTag from '../../Items/WorkOrderItemList/WorkOrderItemTypeTag';
import { Group } from '@/components/_common';
import { useHistory } from 'react-router-dom';

interface Props {
  workorderItems?: WorkOrderItem[];
}

export const hasWorkOrderItemsIntegrationInvalid = (items?: WorkOrderItem[]) =>
  !!items?.some(
    (item) =>
      (item?.isInPecaAiBasket || item.isInWomBasket) &&
      !item?.status &&
      !item.itemId,
  );

export default function BasketItemsIntegrationVerification({
  workorderItems,
}: Props) {
  const history = useHistory();

  const [modalOpened, modalAction] = useDisclosure(false, {
    onOpen() {
      if (history.location.hash === '#items-verification') {
        history.replace(history.location.pathname);
      }
    },
  });

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  useEffect(() => {
    if (history.location.hash === '#items-verification') {
      setTimeout(() => modalAction.open(), 500);
    }
  }, [history.location.hash]);

  const [form] = Form.useForm<PartFormData>();

  const [current, setCurrent] = useState(0);
  const [parts, setParts] = useState<PartFormData[]>([]);

  const items = workorderItems?.filter(
    (item) =>
      (item?.isInPecaAiBasket || item.isInWomBasket) &&
      !item?.status &&
      !item.itemId,
  );

  const hasPecaAiItem = items?.some((item) => !!item.isInPecaAiBasket);
  const hasWomItem = items?.some((item) => !!item.isInWomBasket);

  const currentItem = items?.[current];

  const totalItems = items?.length || 0;
  const currentPosition = current + 1;
  const isFirst = current === 0;
  const isLast = currentPosition === totalItems;

  const hasItemIntagrationInvalid = useMemo(
    () => hasWorkOrderItemsIntegrationInvalid(items),
    [items],
  );

  const partMultipleCreate = usePartMultipleCreate();
  const workorderItemListCreate = useCreateWorkOrderItemList();
  const bindItemsWithOperational =
    useWorkorderComplementBindItemsWithOperational();

  const setValuesOnForm = useCallback(async () => {
    try {
      const item = items?.[current];

      const hasPartIndex = parts.findIndex(
        (part) =>
          part.catalogProductId ===
          (item?.pecaAiProductId || item?.womBasketProductId),
      );

      if (hasPartIndex >= 0) {
        form.setFieldsValue(parts[hasPartIndex]);
      } else {
        form.setFieldsValue({
          name: item?.description,
          catalogProductId: item?.pecaAiProductId || item?.womBasketProductId,
          manufacturingCode: item?.manufacturingCode,
        });
      }
    } catch {}
  }, [current, parts, items]);

  useEffect(() => {
    if (modalOpened) {
      setValuesOnForm();
    }
  }, [setValuesOnForm, modalOpened, current, items]);

  const onPrev = () => setCurrent((prev) => prev - 1);

  const validateAndSetFormValues = useCallback(async () => {
    const values = await form.validateFields();

    const hasPartIndex = parts.findIndex(
      (part) => part.catalogProductId === values.catalogProductId,
    );

    let nextParts = parts;

    if (hasPartIndex >= 0) {
      nextParts = parts.map((part, index) => {
        if (index === hasPartIndex) {
          return values;
        }
        return part;
      });
    } else {
      nextParts = [...parts, values];
    }

    setParts(nextParts);

    return nextParts;
  }, [parts]);

  const onNext = async () => {
    try {
      await validateAndSetFormValues();
      setCurrent((prev) => prev + 1);
    } catch {}
  };

  const onSubmit = async () => {
    try {
      const partsValues = await validateAndSetFormValues();

      if (!complement?.id || !workorder?.id)
        return message.warn('Não existe um complemento!');

      await partMultipleCreate.mutateAsync({
        partRequestDTOList: partsValues.map((part) => ({
          ...part,
          imageUrl: part.imageUrl?.length
            ? part.imageUrl[0].response?.filePath || part.imageUrl[0].url
            : undefined,
          flag: PartFlag.integration,
        })),
      });

      await bindItemsWithOperational.mutateAsync({
        complementId: complement.id,
        workOrderId: workorder.id,
      });

      handleClose();
    } catch {}
  };

  const handleClose = () => {
    form.resetFields();
    setCurrent(0);
    modalAction.close();
  };

  const modalFooter = (
    <Row gutter={[16, 16]} style={{ textAlign: 'left' }} align="middle">
      <Col flex={1}>
        <span>{`${currentPosition} / ${totalItems}`}</span>
      </Col>

      <Col>
        <Button block onClick={handleClose}>
          Cancelar
        </Button>
      </Col>

      {!isFirst && (
        <Col>
          <Button block onClick={onPrev}>
            Voltar
          </Button>
        </Col>
      )}

      <Col>
        {!isLast && (
          <Button type="primary" block onClick={onNext}>
            Próxima peça
          </Button>
        )}

        {isLast && (
          <Button
            type="primary"
            block
            onClick={onSubmit}
            loading={
              partMultipleCreate.isLoading || workorderItemListCreate.isLoading
            }
          >
            Cadastrar & Vincular
          </Button>
        )}
      </Col>
    </Row>
  );

  if (!hasItemIntagrationInvalid) return null;

  return (
    <div id="items-verification">
      <Alert
        type="warning"
        style={{ marginTop: 24, borderRadius: 8 }}
        description={
          <>
            <strong>Há peças sem estoque</strong>

            <p>
              {`Os produtos sincronizados com a cesta do `}
              <strong>
                {hasPecaAiItem && hasWomItem
                  ? 'Peça Aí e Bosch WOM'
                  : hasPecaAiItem
                  ? 'Peça Aí'
                  : 'Bosch WOM'}
              </strong>
              {`, necessitam ser cadastradas na base da oficina para atualizar os dados de estoque.`}
            </p>

            <Button onClick={modalAction.open}>Atualizar peças</Button>
          </>
        }
      />

      <Modal
        open={modalOpened}
        onCancel={modalAction.close}
        title="Cadastro e vinculo de peças de integração"
        width={800}
        footer={modalFooter}
        maskClosable={false}
      >
        <p>
          Informe os dados da peça integrada ao orçamento para cadastrá-la na
          base de dados da sua oficina e manter o estoque atualizado.
        </p>

        {!!currentItem && (
          <Group gap={10} style={{ marginBottom: 24 }}>
            <strong style={{ fontSize: '1.15rem' }}>Cesta:</strong>
            <WorkOrderItemTypeTag workOrderItem={currentItem} />
          </Group>
        )}

        <PartForm
          key={modalOpened ? current : undefined}
          isModal
          isLoading={false}
          form={form}
          disabledFields={{
            name: !!currentItem?.description,
            manufacturingCode: !!currentItem?.manufacturingCode,
          }}
        />
      </Modal>
    </div>
  );
}
